import React, { useEffect, useState } from 'react';
import {
  Box,
  IconButton,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import clsx from 'clsx';
import type { CarBrandList, CarModelList, CarSubTypeList, CarTypeList } from 'entities/car';
import { EditApplicationModal } from 'pages/dashboard/open-application/ui/edit-application-modal';
import { useNavigate, useParams } from 'react-router-dom';
import { apiClient } from 'shared/api';
import ArrowRight from 'shared/assets/icons/arrow_right.svg';
import closeIcon from 'shared/assets/icons/close.svg';
import helpIcon from 'shared/assets/icons/help.svg';
import infoIcon from 'shared/assets/icons/info.svg';
import { ButtonCustom } from 'shared/ui/button-custom';
import { DownloadButton } from 'shared/ui/download-button';
import { DashboardHeaderBackLink } from 'widgets/dashboard-header-back-link';
import styles from './open-app-admin.module.scss';

export const OpenAppAdmin = () => {
  const { type, uid } = useParams();
  const navigate = useNavigate();

  const headerTitleSupplierText = type == 'select' ? 'Поставщик' : 'Лизинговая компания';

  const [appData, setAppData] = useState<any>([]);
  const [rowsSelect, setRowsSelect] = useState<any>([]);
  const [rowsLeasing, setRowsLeasing] = useState<any>([]);
  const [dynamicField, setDynamicField] = useState<any>([]);

  // Состояние для редактирования
  const [editModalOpen, setEditModalOpen] = useState(false);

  // Справочники
  const [typeValueList, setTypeValueList] = useState<CarTypeList>([]);
  const [subTypeValueList, setSubTypeValueList] = useState<CarSubTypeList>([]);
  const [brandValueList, setBrandValueList] = useState<CarBrandList>([]);
  const [modelValueList, setModelValueList] = useState<CarModelList>([]);

  const postData = async () => {
    try {
      const data = { id: uid };
      const url =
        type == 'select' ? '/select-applications/appAdmin' : '/leasing-applications/appAdmin';
      const response = await apiClient.post(url, data);
      setAppData(response?.data?.data?.application);
    } catch (error) {
      console.error('Ошибка при отправке данных:', error);
    }
  };

  // Функции для загрузки справочников
  const fetchCarTypesData = async () => {
    try {
      const response1 = await apiClient.get('/car-types/all');
      const data1 = await response1;
      setTypeValueList([
        ...(data1.data.data as CarTypeList),
        { id: 0, name: 'Иное', value: 'other' },
      ]);
    } catch (error) {
      setTypeValueList([{ id: 0, name: 'Иное', value: 'other' }]);
      console.error('Ошибка при загрузке данных:', error);
    }
  };

  const getBrandsForType = async (carTypeId: number | string) => {
    try {
      const response = await apiClient.post('/car-brands/type', { typeId: carTypeId });
      const data = await response;
      setBrandValueList([
        ...(data.data.data as CarBrandList),
        { id: 0, name: 'Иное', value: 'other' },
      ]);
    } catch (error) {
      console.error('Ошибка при загрузке данных:', error);
    }
  };

  const getSubtypeForType = async (carTypeId: number | string) => {
    try {
      const response = await apiClient.post('/car-subtypes/subtypes', {
        typeId: carTypeId,
      });
      const data = await response;
      setSubTypeValueList([
        ...(data.data.data as CarSubTypeList),
        { id: 0, name: 'Иное', value: 'other' },
      ]);
    } catch (error) {
      console.error('Ошибка при загрузке данных:', error);
    }
  };

  const getBrandsForSubType = async (carSubtypeId: number | string) => {
    try {
      const response = await apiClient.post('/car-brands/subtype', { subtypeId: carSubtypeId });
      const data = await response;
      setBrandValueList([
        ...(data.data.data as CarBrandList),
        { id: 0, name: 'Иное', value: 'other' },
      ]);
    } catch (error) {
      console.error('Ошибка при загрузке данных:', error);
    }
  };

  const getVehicleForBrandAndSubType = async (brand: number | string, subtype: number | string) => {
    try {
      const response = await apiClient.post('/vehicles/brandSubType', {
        brandId: brand,
        subtypeId: subtype,
      });
      const data = await response;
      // eslint-disable-next-line camelcase
      const otherOption = { id: 0, name: 'Иное', car_params: [], value: 'other' };
      setModelValueList([...(data.data.data as CarModelList), otherOption]);
    } catch (error) {
      console.error('Ошибка при загрузке данных:', error);
    }
  };

  // Загружаем справочники при монтировании компонента
  useEffect(() => {
    fetchCarTypesData();
  }, []);

  useEffect(() => {
    if (uid) {
      postData();
    }
  }, [uid]);

  useEffect(() => {
    setDynamicField(appData?.car_params || []);
    if (type == 'select') {
      setRowsSelect(appData?.select_offers || []);
    } else {
      setRowsLeasing(appData?.leasing_offers || []);
    }
  }, [appData]);

  const renderApp = () => {
    if (type == 'select') {
      return (
        <Box className={styles.appWrapper}>
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Статус заявки</span>
            <span className={styles.appDescValue}>
              {appData?.fulfilled === false ? 'Черновик' : appData?.status_app}
            </span>
          </Box>
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Состояние</span>
            <span className={styles.appDescValue}>{appData?.condition}</span>
          </Box>
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Место поставки ТС</span>
            <span className={styles.appDescValue}>{appData?.destination}</span>
          </Box>
          {appData?.currency && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>Валюта</span>
              <span className={styles.appDescValue}>{appData?.currency}</span>
            </Box>
          )}
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Cпособ покупки</span>
            <span className={styles.appDescValue}>{appData?.pay_method}</span>
          </Box>
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Количество ТС</span>
            <span className={styles.appDescValue}>{appData?.quantity}</span>
          </Box>
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Тип ТС</span>
            <span className={styles.appDescValue}>
              {appData?.car_type ? `${appData?.car_type}` : ''}
            </span>
          </Box>{' '}
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Подтип ТС</span>
            <span className={styles.appDescValue}>
              {appData?.car_subtype ? appData?.car_subtype : ''}
            </span>
          </Box>
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Марка</span>
            <span className={styles.appDescValue}>{appData?.car_brand}</span>
          </Box>
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Модель</span>
            <span className={styles.appDescValue}>{appData?.vehicle}</span>
          </Box>
          {dynamicField?.map((field: any) => (
            <Box key={field?.fieldName} className={styles.appDesc}>
              <span className={styles.appDescTitle}>{field?.fieldName}</span>
              <span className={styles.appDescValue}>{field?.fieldValue}</span>
            </Box>
          ))}
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Бюджет на покупку</span>
            <span className={styles.appDescValue}>
              {new Intl.NumberFormat('ru-RU').format(appData?.budget as number)} ₽
            </span>
          </Box>
          {appData?.inn && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>ИНН</span>
              <span className={styles.appDescValue}>{appData?.inn}</span>
            </Box>
          )}
          {appData?.phone && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>Телефон</span>
              <span className={styles.appDescValue}>{appData?.phone}</span>
            </Box>
          )}
          {appData?.email && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>E-mail</span>
              <span className={styles.appDescValue}>{appData?.email}</span>
            </Box>
          )}
          {appData?.bio && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>ФИО</span>
              <span className={styles.appDescValue}>{appData?.bio}</span>
            </Box>
          )}
          {appData?.kp && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>Имеющееся предложение</span>
              <DownloadButton fileUrl={appData?.kp?.url} title={appData?.kp?.name} />
            </Box>
          )}
          {appData?.agent_inn && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>ИНН агента</span>
              <span className={styles.appDescValue}>{appData?.agent_inn ?? '-'}</span>
            </Box>
          )}
          {appData?.agent_phone && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>Телефон агента</span>
              <span className={styles.appDescValue}>{appData?.agent_phone ?? '-'}</span>
            </Box>
          )}
          {appData?.agent_email && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>E-mail агента</span>
              <span className={styles.appDescValue}>{appData?.agent_email ?? '-'}</span>
            </Box>
          )}
          {appData?.agent_bio && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>ФИО агента</span>
              <span className={styles.appDescValue}>{appData?.agent_bio ?? '-'}</span>
            </Box>
          )}
        </Box>
      );
    }
    if (type == 'leasing') {
      return (
        <Box className={styles.appWrapper}>
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Статус заявки</span>
            <span className={styles.appDescValue}>
              {appData?.fulfilled === false ? 'Черновик' : appData?.status_app}
            </span>
          </Box>
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Траснспортное средство</span>
            <span className={styles.appDescValue}>
              {appData?.car_type ? `${appData?.car_type} - ` : ''}{' '}
              {appData?.car_subtype ? appData?.car_subtype : ''}
            </span>
          </Box>
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Марка</span>
            <span className={styles.appDescValue}>{appData?.car_brand}</span>
          </Box>
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Модель</span>
            <span className={styles.appDescValue}>{appData?.vehicle}</span>
          </Box>

          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Количество ТС</span>
            <span className={styles.appDescValue}>{appData?.quantity}</span>
          </Box>

          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Стоимость предмета лизинга</span>
            <span className={styles.appDescValue}>
              {new Intl.NumberFormat('ru-RU').format(appData?.price as number)} ₽
            </span>
          </Box>
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Первоначальный взнос</span>
            <span className={styles.appDescValue}>{appData?.initial_payment} %</span>
          </Box>
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Срок лизинга</span>
            <span className={styles.appDescValue}>{appData?.lease_period} мес.</span>
          </Box>
          <Box className={styles.appDesc}>
            <span className={styles.appDescTitle}>Тип оплаты</span>
            <span className={styles.appDescValue}>{appData?.payment_type}</span>
          </Box>
          {appData?.currency && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>Валюта</span>
              <span className={styles.appDescValue}>{appData?.currency}</span>
            </Box>
          )}

          {appData?.inn && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>ИНН</span>
              <span className={styles.appDescValue}>{appData?.inn}</span>
            </Box>
          )}

          {appData?.phone && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>Телефон</span>
              <span className={styles.appDescValue}>{appData?.phone}</span>
            </Box>
          )}

          {appData?.email && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>E-mail</span>
              <span className={styles.appDescValue}>{appData?.email}</span>
            </Box>
          )}

          {appData?.bio && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>ФИО</span>
              <span className={styles.appDescValue}>{appData?.bio}</span>
            </Box>
          )}

          {appData?.kp && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>Имеющееся предложение</span>
              <DownloadButton fileUrl={appData?.kp?.url} title={appData?.kp?.name} />
            </Box>
          )}

          {appData?.agent_inn && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>ИНН агента</span>
              <span className={styles.appDescValue}>{appData?.agent_inn ?? '-'}</span>
            </Box>
          )}
          {appData?.agent_phone && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>Телефон агента</span>
              <span className={styles.appDescValue}>{appData?.agent_phone ?? '-'}</span>
            </Box>
          )}
          {appData?.agent_email && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>E-mail агента</span>
              <span className={styles.appDescValue}>{appData?.agent_email ?? '-'}</span>
            </Box>
          )}
          {appData?.agent_bio && (
            <Box className={styles.appDesc}>
              <span className={styles.appDescTitle}>ФИО агента</span>
              <span className={styles.appDescValue}>{appData?.agent_bio ?? '-'}</span>
            </Box>
          )}
        </Box>
      );
    }
  };

  const renderHeaderRow = () => {
    return (
      <TableRow className={styles.tableHeadRow}>
        <TableCell className={`${styles.tableHeadCellFirst} ${styles.tableHeadCell}`} align='left'>
          № предложения
        </TableCell>
        <TableCell className={`${styles.tableHeadCell} ${styles.tableCellMobile}`} align='left'>
          Марка
        </TableCell>
        <TableCell className={`${styles.tableHeadCell} ${styles.tableCellMobile}`} align='left'>
          Модель
        </TableCell>
        <TableCell className={styles.tableHeadCell} align='left'>
          Стоимость
        </TableCell>
        <TableCell className={`${styles.tableHeadCell} ${styles.tableCellMobile}`} align='left'>
          {headerTitleSupplierText}
        </TableCell>
        <TableCell
          className={`${styles.tableHeadCellLast} ${styles.tableHeadCell}`}
          align='right'
        />
      </TableRow>
    );
  };

  const [openTooltipId, setOpenTooltipId] = useState<any | null>(null);

  const handleTooltipOpen = (id: any) => {
    setOpenTooltipId(id);
  };

  const handleTooltipClose = () => {
    setOpenTooltipId(null);
  };

  const handleCanceled = async () => {
    try {
      if (!appData?.id || !type) return;

      const confirm = window.confirm('Вы уверены, что хотите отменить заявку?');
      if (!confirm) return;

      const data = { id: appData?.id };
      const url = type == 'select' ? '/select-applications/cancel' : '/leasing-applications/cancel';
      await apiClient.post(url, data);
      window.location.reload();
    } catch (error) {
      console.error('Ошибка при отправке данных:', error);
    }
  };

  const handleEdit = () => {
    setEditModalOpen(true);
  };

  const handleApproveDraft = async () => {
    await postData();
  };

  const handleSaveSuccess = async () => {
    await postData();
  };

  const renderRows = (rows: any) => {
    const filteredRows = rows.filter((row: any) => row.status_filled === true);

    return filteredRows.map((row: any) => (
      <TableRow
        className={clsx([`${styles.tableBodyRow}`, row.status_accept && `${styles.selectedRow}`])}
        key={row.id}
        sx={{ '& td, & th': { border: 0 }, marginTop: '10px' }}
      >
        <TableCell
          className={`${styles.tableBodyCellFirst} ${styles.tableBodyCell}`}
          component='th'
          scope='row'
        >
          {row.id}
        </TableCell>
        <TableCell className={`${styles.tableBodyCell} ${styles.tableCellMobile}`} align='left'>
          {row.car_brand}
        </TableCell>
        <TableCell className={`${styles.tableBodyCell} ${styles.tableCellMobile}`} align='left'>
          {row.vehicle || row.car_model}
        </TableCell>
        <TableCell className={styles.tableBodyCell} align='left'>
          {new Intl.NumberFormat('ru-RU').format(row.price as number)} ₽
        </TableCell>
        <TableCell className={`${styles.tableBodyCell} ${styles.tableCellMobile}`} align='left'>
          <div className={styles.nameTooltipWrapper}>
            {row.name}
            <div className={styles.tooltip}>
              <Tooltip
                onClose={handleTooltipClose}
                open={openTooltipId == row.id}
                disableFocusListener
                arrow
                disableHoverListener
                disableTouchListener
                title={
                  <>
                    <div className={styles.tooltipWrapper}>
                      <div className={styles.tooltipHeader}>
                        <SvgIcon
                          className={styles.infoSvg}
                          component={infoIcon}
                          inheritViewBox
                          sx={{ fontSize: '19px' }}
                        />
                        <span className={styles.tooltipHeaderText}>Контакты контрагента</span>
                        <IconButton
                          disableRipple
                          sx={{
                            width: '10px',
                            height: '10px',
                            marginRight: 0,
                          }}
                          onClick={handleTooltipClose}
                          edge='end'
                          className={styles.svgClose}
                        >
                          <SvgIcon component={closeIcon} inheritViewBox sx={{ fontSize: '10px' }} />
                        </IconButton>
                      </div>
                      <div className={styles.tooltipBody}>
                        <span className={styles.tooltipBodyText}>Компания: {row.name}</span>
                        <span className={styles.tooltipBodyText}>Эл. почта: {row.email}</span>
                        <span className={styles.tooltipBodyText}>Ф.И.О менеджера: {row.bio}</span>
                        <span className={styles.tooltipBodyText}>Тел.: {row.phone}</span>
                      </div>
                    </div>
                  </>
                }
                slotProps={{
                  popper: {
                    disablePortal: true,
                  },
                  tooltip: {
                    sx: {
                      '&': {
                        border: '1px solid #CFE2E8',
                        backgroundColor: '#F8FAFC',
                        borderRadius: '10px',
                        padding: '16px',
                      },
                    },
                  },
                  arrow: {
                    sx: {
                      '&': {
                        ':before': {
                          border: '1px solid #CFE2E8',
                        },
                        color: '#F8FAFC',
                      },
                    },
                  },
                }}
              >
                <IconButton
                  disableRipple
                  sx={{
                    width: '17px',
                    height: '17px',
                    marginRight: 0,
                  }}
                  onClick={() => handleTooltipOpen(row.id)}
                  edge='end'
                  className={styles.svgIconTooltip}
                >
                  <SvgIcon component={helpIcon} inheritViewBox sx={{ fontSize: '17px' }} />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </TableCell>
        <TableCell className={`${styles.tableBodyCellLast} ${styles.tableBodyCell}`} align='right'>
          <IconButton
            disableRipple
            sx={{
              width: '40px',
              height: '40px',
            }}
            onClick={() => navigate(`/dashboard/admin/offer/${type}/${row.documentId}`)}
            edge='end'
            className={styles.svgIcon}
          >
            <SvgIcon component={ArrowRight} inheritViewBox sx={{ fontSize: '18px' }} />
          </IconButton>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <Box className={styles.openApp}>
      <DashboardHeaderBackLink link={-1} title={`Заявка № ${appData.id}`} />
      {renderApp()}
      {
        <Box className={styles.buttonContainer}>
          <ButtonCustom
            disabled={!['Новая'].includes(appData.status_app)}
            className={styles.button}
            onClick={handleCanceled}
          >
            Отменить заявку
          </ButtonCustom>
          <ButtonCustom
            disabled={!['Новая'].includes(appData.status_app)}
            className={styles.button}
            onClick={handleEdit}
          >
            Редактировать заявку
          </ButtonCustom>
          {appData?.fulfilled === false && (
            <ButtonCustom className={styles.button} onClick={handleEdit}>
              Подтвердить черновик
            </ButtonCustom>
          )}
        </Box>
      }
      {appData.fulfilled !== false ? (
        <Box className={styles.offerTableBlock}>
          <span className={styles.offerHeader}>Предложения по заявке</span>
          <Box>
            <TableContainer component={Box} className={styles.tableContainer}>
              <Table className={styles.table} sx={{ minWidth: 1070 }} aria-label={uid}>
                <TableHead className={styles.tableHead}>{renderHeaderRow()}</TableHead>
                <TableBody className={styles.tableBody}>
                  {type == 'select' ? renderRows(rowsSelect) : renderRows(rowsLeasing)}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      ) : null}

      {/* Модальное окно редактирования */}
      <EditApplicationModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        appData={appData}
        type={type}
        onSave={handleSaveSuccess}
        typeValueList={typeValueList}
        subTypeValueList={subTypeValueList}
        brandValueList={brandValueList}
        modelValueList={modelValueList}
        onLoadSubtypes={getSubtypeForType}
        onLoadBrandsForType={getBrandsForType}
        onLoadBrandsForSubtype={getBrandsForSubType}
        onLoadModels={getVehicleForBrandAndSubType}
      />
    </Box>
  );
};
